import { strings } from '@/strings';

/**
 * ISO-style and Latin-digit on purpose: an Arabic-Indic date in a table column
 * that also holds counts makes the two hard to tell apart at a glance, and
 * every recruiter reading this screen also reads the console.
 */
export function formatDate(ms: number | null | undefined): string {
  if (!ms) return '—';
  const d = new Date(ms);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

const STAGE_LABELS: Record<string, string> = {
  nominated: strings.stageNominated,
  contacted: strings.stageContacted,
  interviewed: strings.stageInterviewed,
  diagnosed: strings.stageDiagnosed,
  vetted: strings.stageVetted,
  matched: strings.stageMatched,
  active: strings.stageActive,
  inactive: strings.stageInactive,
  withdrawn: strings.stageWithdrawn,
};

const STATUS_LABELS: Record<string, string> = {
  submitted: strings.statusSubmitted,
  contacted: strings.statusContacted,
  interested: strings.statusInterested,
  not_now: strings.statusNotNow,
  declined: strings.statusDeclined,
  promoted: strings.statusPromoted,
  rejected: strings.statusRejected,
};

/**
 * An unknown key shows itself rather than an empty cell, so a new stage that
 * nobody added a label for is visibly missing instead of invisibly blank.
 */
export function stageLabel(stage: string): string {
  return STAGE_LABELS[stage] || stage;
}

export function statusLabel(status: string): string {
  return STATUS_LABELS[status] || status;
}

const OPPORTUNITY_STATUS_LABELS: Record<string, string> = {
  draft: strings.oppStatusDraft,
  submitted: strings.oppStatusSubmitted,
  under_review: strings.oppStatusUnderReview,
  live: strings.oppStatusLive,
  paused: strings.oppStatusPaused,
  filled: strings.oppStatusFilled,
  closed: strings.oppStatusClosed,
};

export function opportunityStatusLabel(status: string): string {
  return OPPORTUNITY_STATUS_LABELS[status] || status;
}

const MATCH_STATUS_LABELS: Record<string, string> = {
  proposed: strings.matchStatusProposed,
  candidate_accepted: strings.matchStatusCandidateAccepted,
  candidate_declined: strings.matchStatusCandidateDeclined,
  org_accepted: strings.matchStatusOrgAccepted,
  org_declined: strings.matchStatusOrgDeclined,
  active: strings.matchStatusActive,
  completed: strings.matchStatusCompleted,
  ended_early: strings.matchStatusEndedEarly,
};

export function matchStatusLabel(status: string): string {
  return MATCH_STATUS_LABELS[status] || status;
}

/** A 0..1 score as a percentage, for a column a human scans. */
export function scorePercent(value: number): string {
  return `${Math.round((Number(value) || 0) * 100)}٪`;
}

const ROLE_LABELS: Record<string, string> = {
  admin: strings.roleAdmin,
  recruiter: strings.roleRecruiter,
  recommender: strings.roleRecommender,
  'ngo-rep': strings.roleNgoRep,
  candidate: strings.roleCandidate,
  guest: strings.roleGuest,
};

/**
 * An unknown role shows itself rather than an empty cell — the same rule the
 * stage and status labels follow. A role that exists in a token but has no
 * label here is a bug worth seeing, not worth hiding.
 */
export function roleLabel(role: string): string {
  return ROLE_LABELS[role] || role;
}

const DAY_MS = 86_400_000;

/**
 * Whole days between two moments, or null when the question cannot be answered.
 *
 * Null rather than 0 for a missing end: a candidate who has not been vetted has
 * no time-to-vetted, and counting them as zero days would report the funnel as
 * instant. A backwards span is also null — it means the data is wrong, and a
 * negative duration in a report is worse than an absent one.
 */
export function daysBetween(from: number | null | undefined, to: number | null | undefined): number | null {
  if (!from || !to) return null;
  const span = to - from;
  return span < 0 ? null : Math.floor(span / DAY_MS);
}

/**
 * The median, deliberately not the mean: one case that sat in a queue for a
 * year would drag an average far past anything a real person experiences.
 */
export function medianDays(spans: number[]): number | null {
  if (spans.length === 0) return null;
  const sorted = [...spans].sort((a, b) => a - b);
  const mid = Math.floor(sorted.length / 2);
  return sorted.length % 2 === 1 ? sorted[mid] : (sorted[mid - 1] + sorted[mid]) / 2;
}

/**
 * The closed answers the two forms offer, as Arabic labels.
 *
 * Every one of these is stored as an id and rendered through here, so a
 * reworded button is one edit in strings.ts rather than a data migration. An
 * unknown id shows itself, the same rule the other label maps follow.
 */
const ZONE_LABELS: Record<string, string> = {
  north: strings.zoneNorth,
  east: strings.zoneEast,
  west: strings.zoneWest,
  south: strings.zoneSouth,
  central: strings.zoneCentral,
  outside: strings.zoneOutside,
};

const MODE_LABELS: Record<string, string> = {
  onsite: strings.modeOnsite,
  remote: strings.modeRemote,
  hybrid: strings.modeHybrid,
  field: strings.modeField,
};

const WORK_STYLE_LABELS: Record<string, string> = {
  individual: strings.oppStyleIndividual,
  team: strings.oppStyleTeam,
  both: strings.workStyleBoth,
};

const STUDY_LABELS: Record<string, string> = {
  early_years: strings.studyEarlyYears,
  final_year: strings.studyFinalYear,
  graduated_within_two: strings.studyGraduatedWithinTwo,
  graduated_over_two: strings.studyGraduatedOverTwo,
};

const EMPLOYMENT_LABELS: Record<string, string> = {
  studying_only: strings.employmentStudyingOnly,
  studying_part_time: strings.employmentStudyingPartTime,
  not_working: strings.employmentNotWorking,
  part_time: strings.employmentPartTime,
  full_time: strings.employmentFullTime,
};

const FIELD_STATUS_LABELS: Record<string, string> = {
  active: strings.fieldStatusActive,
  away_under_year: strings.fieldStatusAwayUnderYear,
  away_over_year: strings.fieldStatusAwayOverYear,
};

const TIME_LABELS: Record<string, string> = {
  morning: strings.timeMorning,
  evening: strings.timeEvening,
  by_agreement: strings.timeByAgreement,
};

const FLEXIBILITY_LABELS: Record<string, string> = {
  fixed: strings.flexFixed,
  flexible: strings.flexFlexible,
  by_tasks: strings.flexByTasks,
};

const DURATION_LABELS: Record<string, string> = {
  three_months: strings.durationThreeMonths,
  six_months: strings.durationSixMonths,
  one_year: strings.durationOneYear,
  ongoing: strings.durationOngoing,
  project_bound: strings.durationProjectBound,
};

const PRIORITY_LABELS: Record<string, string> = {
  urgent: strings.priorityUrgent,
  within_month: strings.priorityWithinMonth,
  within_quarter: strings.priorityWithinQuarter,
  flexible: strings.priorityFlexible,
};

const FUNDING_LABELS: Record<string, string> = {
  org: strings.fundingOrg,
  donor: strings.fundingDonor,
  other: strings.fundingOther,
  not_applicable: strings.fundingNotApplicable,
};

const CADENCE_LABELS: Record<string, string> = {
  weekly_meeting: strings.cadenceWeeklyMeeting,
  monthly_report: strings.cadenceMonthlyReport,
  deliverables: strings.cadenceDeliverables,
  other: strings.cadenceOther,
};

function labeller(map: Record<string, string>) {
  return (id: string): string => map[id] || id;
}

export const zoneLabel = labeller(ZONE_LABELS);
export const modeLabel = labeller(MODE_LABELS);
export const workStyleLabel = labeller(WORK_STYLE_LABELS);
export const studyStatusLabel = labeller(STUDY_LABELS);
export const employmentStatusLabel = labeller(EMPLOYMENT_LABELS);
export const fieldStatusLabel = labeller(FIELD_STATUS_LABELS);
export const timeLabel = labeller(TIME_LABELS);
export const flexibilityLabel = labeller(FLEXIBILITY_LABELS);
export const durationLabel = labeller(DURATION_LABELS);
export const priorityLabel = labeller(PRIORITY_LABELS);
export const fundingLabel = labeller(FUNDING_LABELS);
export const cadenceLabel = labeller(CADENCE_LABELS);

/** A list of ids as one readable line: "حضوري · هجين". */
export function joinLabels(ids: string[], label: (id: string) => string): string {
  return (ids || []).map(label).join(' · ') || '—';
}

/** الحد الأدنى للخبرة, where null means the card set no threshold. */
export function minYearsLabel(years: number | null | undefined): string {
  if (years === null || years === undefined) return strings.oppMinYearsNone;
  return `${years} ${strings.unitYears}`;
}
